/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { get } from '../../../services/GenesisApi';
import { GET_CONTRACTS, SHIPPER_ACTUAL_DELIVERIES } from '../../../constants/Cva';
import {
  IShipperActualDeliveries,
  IShippers,
  ICounterpartyList,
} from '../../../types/ShipperActuals.type';

export interface IShipperActualDeliveriesState {
  loadingShippers: boolean;
  shippers: IShippers[];
  loadingShipperActualDeliveries: boolean;
  shipperActualDeliveries: IShipperActualDeliveries[];
  counterpartyList: ICounterpartyList[];
}

export const initialState: IShipperActualDeliveriesState = {
  loadingShippers: false,
  shippers: [],
  loadingShipperActualDeliveries: true,
  shipperActualDeliveries: [],
  counterpartyList: [],
};

export const getAllShippers = createAsyncThunk(
  'shipperActualDeliveries/getAllShippers',
  async () => {
    return get(GET_CONTRACTS, {})
      .then((response) => {
        const data = response.data || [];
        return data.sort((a, b) => a.name.localeCompare(b.name));
      })
      .catch((error) => {
        throw error;
      });
  },
);

export const getAllShipperActualDeliveries = createAsyncThunk(
  'shipperActualDeliveries/getAllShipperActualDeliveries',
  async (payload: { gasDay: string; contractIds: number[] }) => {
    const { gasDay, contractIds } = payload;

    return get(
      `${SHIPPER_ACTUAL_DELIVERIES}?gasDay=${gasDay}&contractIds=${contractIds.join(',')}`,
      {},
    )
      .then((response) => {
        const data = response.data || [];
        return data.map((_) => {
          return {
            ..._,
            updateDelivery: null,
            updateDayAheadNomination: null,
            isUpdatedDANomination: false,
            isUpdatedDelivery: false,
            deliveryFinalNoms: _.delivery && _.nomination ? _.delivery - _.nomination : null,
            deliveryFinalDANoms:
              _.delivery && _.dayAheadNomination ? _.delivery - _.dayAheadNomination : null,
          };
        });
      })
      .catch((error) => {
        throw error;
      });
  },
);

const shipperActualDeliveries = createSlice({
  name: 'shipperActualDeliveries',
  initialState,
  reducers: {
    updateActual: (state, action) => {
      const {
        payload: { actual, value, isUpdated },
      } = action;
      state.shipperActualDeliveries = state.shipperActualDeliveries.map((_) => {
        if (_.id === actual.id && _.gasDay === actual.gasDay) {
          return {
            ..._,
            updateDelivery: value,
            isUpdatedDelivery: isUpdated,
          };
        }
        return _;
      });
    },
    resetUpdatedActual: (state) => {
      state.shipperActualDeliveries = state.shipperActualDeliveries.map((_) => {
        return {
          ..._,
          updateDelivery: null,
          isUpdatedDelivery: false,
          updateDayAheadNomination: null,
          isUpdatedDANomination: false,
        };
      });
    },
    updatedAllActuals: (state, action) => {
      const {
        payload: { data = [] },
      } = action;
      state.shipperActualDeliveries = data.map((_) => ({
        ..._,
        updateDelivery: null,
        isUpdatedDelivery: false,
      }));
    },
    copyPastActuals: (state, action) => {
      const { data = [], index: selectedIndex } = action.payload;
      state.shipperActualDeliveries = state.shipperActualDeliveries.map((delivery, index) => {
        if (index >= selectedIndex) {
          return {
            ...delivery,
            updateDelivery: data[index - selectedIndex] ?? null,
            isUpdatedDelivery: true,
          };
        }
        return delivery;
      });
    },
    updateDANomination: (state, action) => {
      const {
        payload: { actual, value, isUpdated },
      } = action;
      state.shipperActualDeliveries = state.shipperActualDeliveries.map((_) => {
        if (_.id === actual.id && _.gasDay === actual.gasDay) {
          return {
            ..._,
            updateDayAheadNomination: value,
            isUpdatedDANomination: isUpdated,
          };
        }
        return _;
      });
    },
    copyPastDANominations: (state, action) => {
      const { data = [], index: selectedIndex } = action.payload;
      state.shipperActualDeliveries = state.shipperActualDeliveries.map((delivery, index) => {
        if (index >= selectedIndex) {
          return {
            ...delivery,
            updateDayAheadNomination: data[index - selectedIndex] ?? null,
            isUpdatedDANomination: true,
          };
        }
        return delivery;
      });
    },
    resetShipperActuals: (state) => {
      state.loadingShipperActualDeliveries = false;
      state.shipperActualDeliveries = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllShippers.pending, (state: IShipperActualDeliveriesState) => {
        state.loadingShippers = true;
        state.shippers = [];
      })
      .addCase(
        getAllShippers.fulfilled,
        (state: IShipperActualDeliveriesState, { payload }: PayloadAction<IShippers[]>) => {
          state.loadingShippers = false;
          state.shippers = payload;
          const cpList = payload.reduce((acc: ICounterpartyList[], curr) => {
            if (!acc.some((item) => item.id === curr.counterpartyId)) {
              acc.push({
                id: curr.counterpartyId,
                name: curr.counterpartyName,
              });
            }
            return acc;
          }, []);
          // Add All Counterparties as first option
          cpList.sort((a, b) => a.name.localeCompare(b.name));
          state.counterpartyList = [{ id: Infinity, name: 'All Counterparties' }, ...cpList];
        },
      )
      .addCase(getAllShippers.rejected, (state: IShipperActualDeliveriesState) => {
        state.loadingShippers = false;
        state.shippers = [];
      })

      .addCase(getAllShipperActualDeliveries.pending, (state: IShipperActualDeliveriesState) => {
        state.loadingShipperActualDeliveries = true;
        state.shipperActualDeliveries = [];
      })
      .addCase(
        getAllShipperActualDeliveries.fulfilled,
        (
          state: IShipperActualDeliveriesState,
          { payload }: PayloadAction<IShipperActualDeliveries[]>,
        ) => {
          state.loadingShipperActualDeliveries = false;
          state.shipperActualDeliveries = payload;
        },
      )
      .addCase(getAllShipperActualDeliveries.rejected, (state: IShipperActualDeliveriesState) => {
        state.loadingShipperActualDeliveries = false;
        state.shipperActualDeliveries = [];
      });
  },
});

export const shipperActualDeliveriesActions = shipperActualDeliveries.actions;
export default shipperActualDeliveries.reducer;
